import React from 'react';

import { EventArea } from '../../../../analytics-library/entry/sendEvent';
import { ActionType, getActionType } from '../../../../sharedModules/getActionType';
import { getFormattedPrice, getCurrencySymbol } from '../../../../sharedModules/getFormattedPrice';
import { getPriceFromDeal } from '../../../../sharedModules/getPriceFromDeal';
import type { Deal } from '../../../types/Deal';
import getPriceType from '../../../utils/getPriceType';
import { useHawkWidgetContext } from '../HawkWidget/HawkWidgetContext';

import DisplayPriceContractType from './DisplayPriceContractType';
import styles from './styles/display-price.css';

interface DisplayPriceProps {
  deal: Deal;
  className?: string;
  type?: string;
  label?: string;
  showContractType?: boolean;
  showOfferLabel?: boolean;
  forceAppear?: boolean;
  wrapInLink?: boolean;
  useCostFreeText?: boolean;
  actionType?: ActionType;
  isBlurred?: boolean;
}

const DisplayPrice: React.FC<DisplayPriceProps> = ({
  className,
  showOfferLabel,
  forceAppear,
  deal,
  type,
  label = '',
  showContractType,
  wrapInLink = true,
  useCostFreeText = false,
  actionType,
  isBlurred = false,
}) => {
  const {
    translate,
    language,
    showPrice,
    showWasPrice,
    showPriceRounded,
    genericSharedComponents: { AffiliateLink },
    articleUrl,
    editorialPrice,
  } = useHawkWidgetContext();

  type = type || getPriceType(deal);
  showContractType =
    typeof showContractType === 'undefined'
      ? ['monthly', 'yearly'].indexOf(type) >= 0
      : showContractType;

  const containerStyle = className && styles[className] ? className : 'container';
  const price = getPriceFromDeal(deal, type);

  const formattedPrice = getFormattedPrice({
    price,
    currencyIso: deal.offer.currency_iso,
    showOfferLabel,
    type,
    locale: language,
    translate,
    showPriceRounded,
    useConstFreeText: useCostFreeText,
    label: deal.offer.label,
  });

  /*
    Show the price if showPrice is enabled, and we have a price
    Show a wasPrice if showWasPrice has a valid value, showPrice is enabled and we have a price
  */
  if (
    (showPrice === 'showPrice' ||
      showPrice === 'stream' ||
      showPrice === 'watchAt' ||
      editorialPrice ||
      forceAppear) &&
    formattedPrice &&
    (type !== 'wasPrice' ||
      (type === 'wasPrice' &&
        ['price', 'allPercentage', 'allSaving', 'percentagePrice'].indexOf(showWasPrice) >= 0))
  ) {
    const symbol = getCurrencySymbol(deal.offer.currency_iso, language);

    const mainComponent = (
      <span
        className={styles[containerStyle]}
        data-type={deal?.offer?.label && showOfferLabel ? 'buySimilar' : type}
      >
        {' '}
        {label && <span className={styles['label']}>{label}</span>}
        {isBlurred ? (
          <span className={styles['price']}>
            {symbol && <span className={styles['currency-symbol']}>{symbol}</span>}
            {symbol && ' '}
            <span className={styles['blurred-value']}>XX.XX</span>
          </span>
        ) : (
          <span className={styles['price']}>{formattedPrice}</span>
        )}
        {showContractType && (
          <DisplayPriceContractType deal={deal} type={type} className={className} />
        )}
      </span>
    );

    return wrapInLink ? (
      <AffiliateLink
        deal={deal}
        actionType={getActionType({
          link: deal.offer.link,
          articleUrl,
          productType: deal.product_type,
          actionType,
        })}
        areaClicked={EventArea.PRICE}
        className={className}
      >
        {mainComponent}
      </AffiliateLink>
    ) : (
      mainComponent
    );
  }

  return null;
};

export default DisplayPrice;
